// One avatar for the whole app: picture if there is one, initial if not, and a small
// badge that tells you at a glance whether this is a bot (B) or — optionally — a human (H).
import React from 'react';
import { View, Image, StyleSheet } from 'react-native';
import { Text } from './Text';
import { mediaSource, senderName, Room, isServiceUser } from '../matrix';
import { makeStyles, useTheme } from '../theme';

export type AvatarKind = 'agent' | 'human' | 'room' | 'space';

export function Avatar({ name, mxc, kind, size = 40, humanBadge = false, me = false }: {
  name: string; mxc?: string; kind: AvatarKind; size?: number; humanBadge?: boolean; me?: boolean;
}) {
  const theme = useTheme(); const s = useStyles();
  const src = mxc ? mediaSource(mxc, { w: size * 3, h: size * 3 }) : null;
  const radius = Math.round(size * 0.28);
  const bg = kind === 'agent' ? theme.agent : kind === 'space' ? theme.surface2 : me ? theme.surface2 : theme.accent;
  const fg = kind === 'space' || me ? theme.ink : kind === 'agent' ? theme.agentInk : theme.accentInk;
  const glyph = kind === 'room' ? '#' : (name.trim()[0] || '?').toUpperCase();
  const badge = kind === 'agent' ? 'B' : kind === 'human' && humanBadge ? 'H' : null;
  const badgeSize = Math.max(14, Math.round(size * 0.42));
  return (
    <View style={{ width: size, height: size }}>
      {src ? (
        <Image source={src} style={{ width: size, height: size, borderRadius: radius, backgroundColor: theme.surface2 }} />
      ) : (
        <View style={{ width: size, height: size, borderRadius: radius, backgroundColor: bg, alignItems: 'center', justifyContent: 'center', borderWidth: kind === 'space' ? 1.5 : 0, borderColor: theme.accentBorder }}>
          <Text style={{ color: fg, fontWeight: '800', fontSize: Math.round(size * 0.42) }}>{glyph}</Text>
        </View>
      )}
      {badge && (
        <View style={[s.badge, { width: badgeSize, height: badgeSize, borderRadius: badgeSize / 2, backgroundColor: kind === 'agent' ? theme.agent : theme.accent }]}
          accessibilityLabel={kind === 'agent' ? 'Bot' : 'Human'}>
          <Text style={[s.badgeText, { fontSize: Math.round(badgeSize * 0.62), color: kind === 'agent' ? theme.agentInk : theme.accentInk }]}>{badge}</Text>
        </View>
      )}
    </View>
  );
}

/** What a room "looks like": a 1:1 shows the other party (person or bot); a room with exactly one bot shows the bot; a room with one other member shows them. */
export function roomFace(room: Room, me: string | null, isAgent: (uid: string) => boolean): { name: string; mxc?: string; kind: AvatarKind } {
  if (room.isSpace) return { name: room.name, mxc: room.avatarMxc, kind: 'space' };
  const others = Object.keys(room.members).filter((u) => u !== me && !isServiceUser(u));
  const agents = others.filter(isAgent);
  // A 1:1 shows the other party. A provisioned agent room ("x — FxM Agent") shows its bot even with
  // extra humans inside. Any other group room keeps the room glyph.
  const agentRoom = /FxM Agent\s*$/i.test(room.nameState || '');
  const person = room.isDm ? (others[0] || room.heroes.find((u) => !isServiceUser(u)))
    : others.length === 1 ? others[0]
      : agentRoom && agents.length === 1 ? agents[0]
        : undefined;
  if (person) return { name: senderName(room, person), mxc: room.memberAvatars[person], kind: isAgent(person) ? 'agent' : 'human' };
  return { name: room.name, mxc: room.avatarMxc, kind: 'room' };
}

export function RoomAvatar({ room, me, isAgent, size = 40, humanBadge = false }: {
  room: Room; me: string | null; isAgent: (uid: string) => boolean; size?: number; humanBadge?: boolean;
}) {
  const f = roomFace(room, me, isAgent);
  return <Avatar name={f.name} mxc={f.mxc} kind={f.kind} size={size} humanBadge={humanBadge} />;
}

const useStyles = makeStyles((theme) => StyleSheet.create({
  badge: { position: 'absolute', right: -3, bottom: -3, alignItems: 'center', justifyContent: 'center', borderWidth: 2, borderColor: theme.bg },
  badgeText: { color: theme.accentInk, fontWeight: '900' },
}));
